import {
    Badge,
    Nav,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNotesMedical } from "@fortawesome/free-solid-svg-icons/faNotesMedical";
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { HistoryNavLinkBase, PatientInfoTabName } from '../patientDetailsView';
import { useAppSelector } from '@/store';
import withPermission from '@/components/common/withPermission';
import withConfigEnabler from '@/components/common/withConfigOnOff';
import Mhd2Permissions from '@/constants/permissions';
import { compose } from 'redux';

const PatientInfoNav = (
    {
        activePatientNotes,
        activeCareOrderNotes,
    }: {
        activePatientNotes: string | undefined | null,
        activeCareOrderNotes: string | undefined | null
    }
) => {
    const careOrders = useSelector((state: any) => state.activePatient.careOrderHistory);
    const openCareOrders = careOrders?.filter((careOrder: any) =>
        (careOrder.statusCode !== 'C')
        && (!careOrder.expirationDate || dayjs(careOrder.expirationDate) >= dayjs())
    );

    const openCareOrdersCount = openCareOrders?.length;

    const appointmentHistoryRecords = useSelector((state: any) => state.activePatient.history);
    const recentlyBookedAppointments = appointmentHistoryRecords?.filter((appointment: any) =>
        dayjs(appointment.createDateTime) >= dayjs().subtract(7, 'day')
        && appointment.cancellationDateTime === null
    );

    const recentlyBookedAppointmentsCount = recentlyBookedAppointments?.length;

    const upcomingAppointments = appointmentHistoryRecords?.filter((appointment: any) =>
        dayjs(appointment.appointmentDate) >= dayjs()
        && appointment.cancellationDateTime === null
    );

    const upcomingAppointmentsCount = upcomingAppointments?.length;

    const openWorkflows = useSelector((state: any) => state.activePatient.openWorkflows);
    const unbookedWorkflows = openWorkflows?.filter((workflow: any) => !workflow.appointmentDate)

    const unbookedWorkflowsCount = unbookedWorkflows?.length;

    return (
        <Nav tabs>
            <CareOrderDetailsNavLink activeCareOrderNotes={activeCareOrderNotes} />
            <PatientDetailsNavLink activePatientNotes={activePatientNotes} />
            <CoverageNavLink />
            <UpcomingAppointmentsNavLink upcomingAppointmentsCount={upcomingAppointmentsCount} />
            <AppointmentHistoryNavLink recentlyBookedAppointmentsCount={recentlyBookedAppointmentsCount} />
            <OpenCareOrdersNavLink openCareOrdersCount={openCareOrdersCount} />
            <OpenWorkflowsNavLink unbookedWorkflowsCount={unbookedWorkflowsCount} />
        </Nav>
    )
};

// Care Order Details NavLink Component
interface CareOrderDetailsNavLinkProps {
    activeCareOrderNotes: string | undefined | null;
}
const CareOrderDetailsNavLinkComponent: React.FC<CareOrderDetailsNavLinkProps> = ({ activeCareOrderNotes }) => {
    const careOrderFieldConfig = useSelector((state: any) => state.config.careOrderPanel);

    return (
        <HistoryNavLinkBase
            label="Care Order Details"
            tabName={PatientInfoTabName.patientCareOrderDetails}
            enableCloseActiveTab={true}
        >
            {careOrderFieldConfig.notes.isVisible && activeCareOrderNotes &&
                <span style={{ color: "#426da9", marginLeft: "5px" }}>
                    <FontAwesomeIcon icon={faNotesMedical} />
                </span>
            }
        </HistoryNavLinkBase>
    );
};
const CareOrderDetailsNavLink = compose(
    withPermission(Mhd2Permissions.orderView),
    withConfigEnabler([
        (state) => state.config.orderScheduling.enableOrderDetailsPanel,
        (state) => !!state.careOrder.careOrderVisitIdentifier || !!state.careOrder.externalReferralOrderId
    ])
)(CareOrderDetailsNavLinkComponent) as React.FC<CareOrderDetailsNavLinkProps>;

// Patient Details NavLink Component
interface PatientDetailsNavLinkProps {
    activePatientNotes: string | undefined | null;
}
const PatientDetailsNavLinkComponent: React.FC<PatientDetailsNavLinkProps> = ({ activePatientNotes }) => {
    const patientConfig = useAppSelector((state) => state.config.patient);
    return (
        <HistoryNavLinkBase
            label="Patient Details"
            tabName={PatientInfoTabName.patientDetails}
            enableCloseActiveTab={true}
        >
            {patientConfig.notes.isVisible && activePatientNotes &&
                <span style={{ color: "#426da9", marginLeft: "5px" }}>
                    <FontAwesomeIcon icon={faNotesMedical} />
                </span>
            }
        </HistoryNavLinkBase>
    );
};
const PatientDetailsNavLink = compose<
    React.ComponentType<PatientDetailsNavLinkProps>
>(
    withPermission<PatientDetailsNavLinkProps>(Mhd2Permissions.patientView)
)(PatientDetailsNavLinkComponent);

// Coverage NavLink Component
interface CoverageNavLinkProps { }
const CoverageNavLinkComponent: React.FC<CoverageNavLinkProps> = () => {
    return (
        <HistoryNavLinkBase
            label="Coverage"
            tabName={PatientInfoTabName.coverage}
            enableCloseActiveTab={true}
        />
    );
};
const CoverageNavLink = compose<
    React.ComponentType<CoverageNavLinkProps>
>(
    withPermission<CoverageNavLinkProps>(Mhd2Permissions.patientView),
    withConfigEnabler((state) => state.config.patient.enablePatientAccessCurator && state.config.patient.enablePatientAccessCuratorInsuranceCoverage)
)(CoverageNavLinkComponent);

// Upcoming Appointments NavLink Component
interface UpcomingAppointmentsNavLinkProps {
    upcomingAppointmentsCount: number;
}
const UpcomingAppointmentsNavLinkComponent: React.FC<UpcomingAppointmentsNavLinkProps> = ({
    upcomingAppointmentsCount
}) => {
    return (
        <HistoryNavLinkBase
            label="Upcoming Appointments"
            tabName={PatientInfoTabName.upcomingAppointment}
            enableCloseActiveTab={true}
        >
            {upcomingAppointmentsCount > 0 && (
                <>
                    {' '}
                    <Badge id="upcomingAppointmentsBadge" pill>
                        {upcomingAppointmentsCount}
                    </Badge>
                </>
            )}
        </HistoryNavLinkBase>
    );
};
const UpcomingAppointmentsNavLink = compose<
    React.ComponentType<UpcomingAppointmentsNavLinkProps>
>(
    withPermission<UpcomingAppointmentsNavLinkProps>(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isPatientHistoryEnabled && state.config.patient.showPatientHistoryInPatientInfoPanel)
)(UpcomingAppointmentsNavLinkComponent);

// Appointment History NavLink Component
interface AppointmentHistoryNavLinkProps {
    recentlyBookedAppointmentsCount: number;
}
const AppointmentHistoryNavLinkComponent: React.FC<AppointmentHistoryNavLinkProps> = ({
    recentlyBookedAppointmentsCount
}) => {
    return (
        <HistoryNavLinkBase
            label="Appointment History"
            tabName={PatientInfoTabName.appointmentHistory}
            enableCloseActiveTab={true}
        >
            {recentlyBookedAppointmentsCount > 0 && (
                <>
                    {' '}
                    <Badge id="recentlyBookedAppointmentsBadge" pill>
                        {`${recentlyBookedAppointmentsCount} New`}
                    </Badge>
                </>
            )}
        </HistoryNavLinkBase>
    );
};
const AppointmentHistoryNavLink = compose<
    React.ComponentType<AppointmentHistoryNavLinkProps>
>(
    withPermission<AppointmentHistoryNavLinkProps>(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isPatientHistoryEnabled && state.config.patient.showPatientHistoryInPatientInfoPanel)
)(AppointmentHistoryNavLinkComponent);

// Open Care Orders NavLink Component
interface OpenCareOrdersNavLinkProps {
    openCareOrdersCount: number;
}
const OpenCareOrdersNavLinkComponent: React.FC<OpenCareOrdersNavLinkProps> = ({
    openCareOrdersCount
}) => {
    return (
        <HistoryNavLinkBase
            label="Open Care Orders"
            tabName={PatientInfoTabName.careOrderHistory}
            enableCloseActiveTab={true}
        >
            {openCareOrdersCount > 0 && (
                <>
                    {' '}
                    <Badge id="openCareOrdersBadge" pill>
                        {openCareOrdersCount}
                    </Badge>
                </>
            )}
        </HistoryNavLinkBase>
    );
};
const OpenCareOrdersNavLink = compose<
    React.ComponentType<OpenCareOrdersNavLinkProps>
>(
    withPermission<OpenCareOrdersNavLinkProps>(Mhd2Permissions.orderView),
    withConfigEnabler((state) => state.config.patient.isPatientCareOrderHistoryEnabled)
)(OpenCareOrdersNavLinkComponent);

// Open Workflows NavLink Component
interface OpenWorkflowsNavLinkProps {
    unbookedWorkflowsCount: number;
}
const OpenWorkflowsNavLinkComponent: React.FC<OpenWorkflowsNavLinkProps> = ({
    unbookedWorkflowsCount
}) => {
    return (
        <HistoryNavLinkBase
            label="Open Workflows"
            tabName={PatientInfoTabName.decisionSupportWorkflowHistory}
            enableCloseActiveTab={true}
        >
            {unbookedWorkflowsCount > 0 && (
                <>
                    {' '}
                    <Badge id="unbookedWorkflowsBadge" pill>
                        {unbookedWorkflowsCount}
                    </Badge>
                </>
            )}
        </HistoryNavLinkBase>
    );
};
const OpenWorkflowsNavLink = compose<
    React.ComponentType<OpenWorkflowsNavLinkProps>
>(
    withPermission<OpenWorkflowsNavLinkProps>(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isDecisionSupportWorkflowHistoryEnabled)
)(OpenWorkflowsNavLinkComponent);

export default PatientInfoNav;